import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination, EffectCoverflow } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/effect-coverflow";
import { MapPin, Star, Crown } from "lucide-react";

const newLaunches = [
  {
    name: "Dubai Creek Harbour",
    tagline: "The Future of Urban Living",
    image:
      "https://cdn.properties.emaar.com/wp-content/uploads/2020/03/DUBAI_CREEK_HARBOUR_HERO-1620x832.jpg",
    price: "Starting from AED 4.0M",
    location: "Dubai Creek",
    amenities: [
      "Iconic Tower Views",
      "Marina Access",
      "Retail Promenade",
      "Smart Home",
    ],
  },
  {
    name: "Rivera at The Valley",
    tagline: "Nature Inspires, Vitality Awaits",
    image: "/Images/Rivera_at_valley.jpg",
    price: "Starting from AED 4.78M",
    location: "The Valley, Dubai",
    amenities: [
      "4-Bedroom Townhouses",
      "LEED-Certified",
      "Nature Trails",
      "Flower Farm",
    ],
  },
  {
    name: "Emaar Beachfront",
    tagline: "Luxury Coastal Living",
    image: "https://fnst.axflare.com/community/JPEG/qeNbmFDTmT.jpeg",
    price: "Starting from AED 5.2M",
    location: "Dubai Marina",
    amenities: [
      "Private Beach",
      "Infinity Pools",
      "Waterfront Views",
      "5-Star Amenities",
    ],
  },
  {
    name: "The Valley West",
    tagline: "Green Living, Redefined",
    image: "/Images/GreenLiving.jpg",
    price: "Starting from AED 3.5M",
    location: "The Valley, Dubai",
    amenities: [
      "Sustainable Design",
      "Community Parks",
      "Sports Village",
      "Skatepark",
    ],
  },
];
const BackgroundAnimations = () => {
  const controls1 = useAnimation();
  const controls2 = useAnimation();
  const controls3 = useAnimation();
  const controls4 = useAnimation();

  useEffect(() => {
    const animateBackground = async () => {
      await controls1.start({
        x: [0, 20, -30, 0],
        y: [0, -20, 30, 0],
        opacity: [0.6, 0.7, 0.5, 0.6],
        transition: { duration: 20, repeat: Infinity, repeatType: "reverse" },
      });
    };

    const animateBackground2 = async () => {
      await controls2.start({
        x: [0, -30, 20, 0],
        y: [0, 30, -20, 0],
        opacity: [0.5, 0.6, 0.4, 0.5],
        transition: { duration: 25, repeat: Infinity, repeatType: "reverse" },
      });
    };

    const animateParticles = async () => {
      await controls3.start({
        scale: [1, 1.2, 0.8, 1],
        opacity: [0.8, 1, 0.7, 0.8],
        transition: { duration: 8, repeat: Infinity },
      });
    };

    const animateParticles2 = async () => {
      await controls4.start({
        scale: [1, 0.8, 1.1, 1],
        opacity: [0.6, 0.8, 0.5, 0.6],
        transition: { duration: 12, delay: 2, repeat: Infinity },
      });
    };

    animateBackground();
    animateBackground2();
    animateParticles();
    animateParticles2();
  }, [controls1, controls2, controls3, controls4]);

  return (
    <>
      <motion.div
        animate={controls1}
        initial={{ opacity: 0.6 }}
        className="absolute -top-[30%] -left-[10%] w-[70%] h-[70%] bg-gradient-to-br from-gold-200/30 via-gold-300/20 to-silver-300/30 rounded-full filter blur-[120px]"
      />

      <motion.div
        animate={controls2}
        initial={{ opacity: 0.5 }}
        className="absolute top-[60%] right-[5%] w-[45%] h-[40%] bg-gradient-to-br from-silver-200/30 via-silver-300/20 to-gold-200/10 rounded-full filter blur-[100px]"
      />

      {/* Moving particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-${(i % 3) + 2} h-${(i % 3) + 2} rounded-full 
              ${i % 2 === 0 ? "bg-gold-400/20" : "bg-silver-300/20"}`}
            style={{
              top: `${15 + i * 15}%`,
              left: `${10 + i * 15}%`,
            }}
            animate={{
              y: [0, -20, 20, 0],
              x: [0, 15, -15, 0],
              opacity: [0.6, 0.8, 0.6, 0.6],
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Gold floating elements */}
      <motion.div
        animate={controls3}
        initial={{ opacity: 0.8, scale: 1 }}
        className="absolute top-16 right-[15%] w-4 h-4 rounded-full bg-gold-300/30 backdrop-blur-sm"
      />

      <motion.div
        initial={{ opacity: 0.6, scale: 1 }}
        animate={controls4}
        className="absolute top-32 left-[10%] w-6 h-6 rounded-full bg-silver-400/30 backdrop-blur-sm"
      />

      <motion.div
        animate={{
          y: [0, -15, 0],
          opacity: [0.7, 0.9, 0.7],
          transition: { duration: 6, repeat: Infinity },
        }}
        className="absolute bottom-20 right-[20%] w-5 h-5 rounded-full bg-gold-400/20 backdrop-blur-sm"
      />

      {/* Decorative light beams */}
      <motion.div
        animate={{
          opacity: [0.2, 0.4, 0.2],
          height: ["30%", "40%", "30%"],
          transition: { duration: 8, repeat: Infinity },
        }}
        className="absolute top-0 left-1/4 w-0.5 h-1/3 bg-gradient-to-b from-gold-400/30 to-transparent"
      />

      <motion.div
        animate={{
          opacity: [0.1, 0.3, 0.1],
          height: ["20%", "35%", "20%"],
          transition: { duration: 10, delay: 2, repeat: Infinity },
        }}
        className="absolute top-0 left-3/4 w-0.5 h-1/4 bg-gradient-to-b from-silver-300/30 to-transparent"
      />
    </>
  );
};

const NewLaunches = () => {
  return (
    <section
      className="relative py-20 overflow-hidden bg-gray-50"
      id="launches"
    >
      {/* Enhanced Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <BackgroundAnimations />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto mb-20 px-4 sm:px-6 lg:px-8">
          {/* Subheading */}
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-gold-500 font-semibold text-sm sm:text-base uppercase tracking-widest"
          >
            ✨ New Launches
          </motion.span>

          {/* Main Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="mt-3 text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-gold-600 via-gold-400 to-yellow-300 bg-clip-text text-transparent font-serif drop-shadow-md"
          >
            Emaar's Exclusive Communities
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="text-base sm:text-lg text-gray-600 mt-4 leading-relaxed"
          >
            Discover Emaar's latest developments featuring sustainable living
            and premium amenities designed to elevate your lifestyle.
          </motion.p>

          {/* Gradient Line Divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="w-24 h-1 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600 mx-auto my-6 rounded"
          />

          {/* Animated Glowing Divider with Particles */}
          <div className="relative h-[2px] w-40 sm:w-52 mx-auto my-8 bg-gradient-to-r from-transparent via-gold-400/40 to-transparent">
            {/* Particle 1 */}
            <motion.div
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ duration: 3, repeat: Infinity }}
              className="absolute top-1/2 left-1/4 transform -translate-y-1/2 w-2 h-2 rounded-full bg-gold-400/80 shadow-md"
            />
            {/* Particle 2 */}
            <motion.div
              animate={{
                scale: [1, 1.5, 1],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ duration: 4, delay: 1, repeat: Infinity }}
              className="absolute top-1/2 right-1/4 transform -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-gold-500/80 shadow-md"
            />
          </div>
        </div>
        <Swiper
          modules={[Autoplay, Navigation, Pagination, EffectCoverflow]}
          effect="coverflow"
          coverflowEffect={{
            rotate: 35,
            stretch: 0,
            depth: 100,
            modifier: 1.5,
            slideShadows: true,
          }}
          centeredSlides={true}
          slidesPerView={1}
          navigation
          pagination={{ clickable: true }}
          loop={true}
          loopedSlides={newLaunches.length}
          autoplay={{
            delay: 2000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          breakpoints={{
            640: {
              slidesPerView: 1.5,
              coverflowEffect: {
                rotate: 35,
                depth: 200,
              },
            },
            1024: {
              slidesPerView: 2.5,
              coverflowEffect: {
                rotate: 35,
                depth: 300,
              },
            },
          }}
          className="pb-12"
        >
          {newLaunches.map((launch, index) => (
            <SwiperSlide key={launch.name}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/80 backdrop-blur rounded-xl overflow-hidden shadow-lg group hover:shadow-2xl transition-all duration-300 transform-gpu"
              >
                <div className="relative">
                  <div className="aspect-w-16 aspect-h-9 overflow-hidden">
                    <motion.img
                      src={launch.image}
                      alt={launch.name}
                      className="w-full h-full object-cover"
                      initial={{ scale: 1 }}
                      whileHover={{ scale: 1.1 }}
                      transition={{ duration: 0.6 }}
                      loading={index <= 2 ? "eager" : "lazy"}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    {/* Enhanced hover effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-gold-400/10 to-silver-300/10"
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <div className="absolute top-4 left-4 flex gap-2 flex-wrap">
                    <motion.span
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 }}
                      className="glass-gold px-3 py-1 rounded-full text-sm font-medium text-white shadow-lg backdrop-blur-md flex items-center"
                    >
                      <Crown size={14} className="mr-1.5" />
                      New Launch
                    </motion.span>
                  </div>

                  {/* Animated gradient border */}
                  <motion.div
                    className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-300 via-gold-500 to-silver-300"
                    initial={{ scaleX: 0 }}
                    whileHover={{ scaleX: 1 }}
                    transition={{ duration: 0.6 }}
                  />
                </div>
                <div className="p-6 relative">
                  <motion.h3
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gold-600 transition-colors duration-300"
                  >
                    {launch.name}
                  </motion.h3>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="text-gray-600 mb-4"
                  >
                    {launch.tagline}
                  </motion.p>
                  <div className="flex items-center text-gray-500 text-sm mb-4">
                    <MapPin size={16} className="mr-2 text-gold-500" />
                    {launch.location}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {launch.amenities.map((amenity) => (
                      <motion.span
                        key={amenity}
                        whileHover={{
                          scale: 1.05,
                          backgroundColor: "rgba(210, 172, 71, 0.2)",
                          color: "#8A6D1B",
                        }}
                        className="bg-gold-50 text-gold-600 px-2 py-1 rounded-md text-xs font-medium"
                      >
                        {amenity}
                      </motion.span>
                    ))}
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gold-600 font-semibold flex items-center">
                      <Star size={16} className="mr-1.5" />
                      Starting price upon request
                    </span>

                    {/* Add a subtle animated button */}
                    {/* <motion.button
                      whileHover={{ scale: 1.05, backgroundColor: "#AE8625" }}
                      whileTap={{ scale: 0.95 }}
                      className="px-3 py-1 text-xs bg-gold-400/80 text-white rounded-full transition-colors"
                    >
                      View Details
                    </motion.button> */}
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-400 via-gold-500 to-gold-600 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
              </motion.div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  );
};

export default NewLaunches;
